import React, { useEffect, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { referenceDate } from "../../App/Format"
import { TIMEZONE_OFFSETS_SECONDS } from "../../App/Timezone"
import { useSubscription } from "../../App/useSubscription"
import { BlockRow, BlockField } from "../../Components/Block"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { InfoDialog } from "../../Components/InfoDialog"
import { ILogbookAppStatusResult, IShipSettings } from "../../Types"
import { Subscription } from "../../Types/Generated/LogbookApi/Subscriptions"

interface TimezoneSettingViewProps {
  settings: IShipSettings;
  onEdited: () => void;
  onEditEvent: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
}

// Timezone section of the settings tab: manual-timezone toggle, offset select and
// the resulting current time. Owns its own AppStatus time subscription.
export function TimezoneSettingView({ settings, onEdited, onEditEvent }: TimezoneSettingViewProps) {
  const app = GetApp()!;
  const t = app.getT("ShipSettingsView");
  const namePrefix = `unit.`;

  const [time, setTime] = useState<number>(app.status?.time ? Date.parse(app.status.time) : (new Date().valueOf() - new Date(referenceDate).valueOf()));
  const [timezoneManualValue, setTimezoneManualValue] = useState<number | undefined>(settings?.timezone);
  const [showTimezoneManualInfo, setShowTimezoneManualInfo] = useState(false);

  useEffect(() => {
    setTimezoneManualValue(settings?.timezone);
  }, [settings?.timezone]);

  useSubscription((status: ILogbookAppStatusResult, _type: string) => {
    if (status.time) {
      setTime(Date.parse(status.time));
    }
  }, Subscription.AppStatus);

  const currentTime = useMemo(() => time / 1000 + (timezoneManualValue != undefined ? timezoneManualValue : app.status?.systemTimezone || 0), [time, app.status, timezoneManualValue]);

  return (
    <>
      <BlockRow>
        <h3 className="block-row-followed-by-block-row ">{t("timezone_title")}</h3>
      </BlockRow>
      <BlockRow>
        <LocalizedMessage className="message-text-bigger" message={(m) => m.shipSetting.utc} />
      </BlockRow>
      <BlockRow className="block-row-checkboxes timezone-row">
        <BlockField label={t("timezone_manual")} labelAfter={true} labelFor={`TimezoneManual`} className="block_field_small_16 checkbox-text-bigger timezone-manual-field label-wrap">
          <input id="TimezoneManual" type="checkbox" name={`${namePrefix}.timezoneManualEnabled`} defaultValue={""} onChange={() => { setTimezoneManualValue(timezoneManualValue == undefined ? (app.status?.systemTimezone || 0) : undefined); onEdited(); }} checked={timezoneManualValue != undefined} />
        </BlockField>

        <BlockField label={t("timezone")} className="block_field_small_8 timezone-field">
          {timezoneManualValue != undefined ? (
            <select name="timezone" defaultValue={timezoneManualValue != undefined ? timezoneManualValue : app.status?.systemTimezone || 0} onChange={(e) => { setTimezoneManualValue(parseInt(e.target.value)); onEditEvent(e); }}>
              {TIMEZONE_OFFSETS_SECONDS.map((offset) => {
                return <option key={offset} value={offset}>{app.format("timezone", offset)}</option>;
              })}
            </select>
          ) : (
            <>
              <input name="timezone" type="hidden"value={undefined} />
              <input value={app.format("timezone", app.status?.systemTimezone || 0)} readOnly={true} disabled={true} />
            </>
          )}
        </BlockField>
        <BlockField label={t("current_time")} className="block_field_small_8 current-time-field">
          <input value={app.format("timeUtc", currentTime)} name="time" className="center" readOnly={true} />
        </BlockField>

        {/* Button for more info */}
        <BlockField label=" " className="">
          <button type="button" className="button" onClick={() => setShowTimezoneManualInfo(true)}>
            <span className="question-circle">?</span>  {t("more_info")}
          </button>
        </BlockField>

      </BlockRow>
      {showTimezoneManualInfo && (
        <InfoDialog
          title={t("timezone_manual")}
          message={<LocalizedMessage message={(m) => m.shipSetting.utc_info} />}
          onClose={() => setShowTimezoneManualInfo(false)}
          style="info"
        />
      )}
    </>
  );
}
